import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { createSvgIcon } from "@material-ui/core/utils";
import Brightness4 from "@material-ui/icons/Brightness4";
import AssignmentIndOutlined from "@material-ui/icons/AssignmentIndOutlined";
import AssignmentOutlined from "@material-ui/icons/AssignmentOutlined";
import BugReportOutlined from "@material-ui/icons/BugReportOutlined";
import logo from "../assets/images/Uclusion_Wordmark_Color.svg";

// Same custom icon as uclusion_web_ui/components/CustomChip/LightbulbOutlined.js
const LightbulbOutlined = createSvgIcon(
    <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1-.85.6V16h-4v-2.3l-.85-.6C7.8 12.16 7 10.63 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z" />,
    'LightbulbOutlined',
);

// One looping Stripe-style teaser that replaces the old tabs + slideshow.
// Per J-all-319 / Q-all-151 (O-6) the finished animation has three scenes:
//   1. a static status / plan view (a high-fidelity replica of the real board)
//   2. a cursor glides to "Add job", clicks, and the create-job wizard opens
//      filled from walkthrough step 1 (modeled on /tmp/createJob.png)
//   3. inside the job, AI asks and you pick an option (walkthrough step 2) — not built yet
// Scenes 1 + 2 and the loop between them are built; scene 3 is still to come.

const BLUE = '#2f80ed';
const WIZARD_BLUE = '#2d9cdb';
const GREEN = '#2f855a';
const TOPBAR = '#a9d4d9';
const SIDEBAR = '#8fc5cc';
const BOARD = '#b8e2e5';
const HEADING_TEAL = '#3a5f5e';
const INK = '#1f2933';
const CARD_BORDER = '#cfe1e1';

// Outlined-chip palette from uclusion_web_ui CustomChip/chipStyles.js (C-all-984):
// white fill, colored 1.5px border + colored text. 'new' counts are red, others teal.
const CHIP_TEAL = { text: '#347079', border: '#7fb6bd' };
const CHIP_RED = { text: '#c8362f', border: '#e85757' };

// Scene timeline (ms).
const BOARD_DWELL = 2900;   // hold the static hero so it can be read
const CURSOR_MOVE = 1500;   // cursor glides to the Add job button
const CLICK_HOLD = 480;     // click feedback before the wizard opens
const WIZARD_DWELL = 4600;  // hold the create-job wizard

// Mobile tabs are icon-only, matching uclusion_web_ui PlanningDialog (iconOnlyTabs at sm).
const BOARD_TABS = [
    { label: 'Job Progress', icon: AssignmentIndOutlined, active: true, mobileTag: '2', desktopTag: '2 new' },
    { label: 'Job Backlog', icon: AssignmentOutlined },
    { label: 'Bugs', icon: BugReportOutlined },
    { label: 'Notes / Discussion', icon: LightbulbOutlined },
];

const Wrap = styled.div`
  ${tw`mx-auto w-full`}
  max-width: 70rem;
  text-align: left;
`;

const Backdrop = styled.div`
  ${tw`rounded-2xl p-2 sm:p-4`}
  background: linear-gradient(135deg, #d6eeee 0%, #e6f4f4 60%, #eef7f6 100%);
`;

const AppWindow = styled.div`
  ${tw`rounded-xl overflow-hidden relative`}
  border: 1px solid #bcd9d9;
  box-shadow: 0 24px 50px -16px rgba(31, 41, 51, 0.4);
  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
  background: ${BOARD};
  overflow-x: hidden;
  /* B-Marketing-7: keep the animated cursor (z 40) inside this window's
     stacking context — the header's Blog/Documentation iframe overlay sits at
     z-index 5 and must cover it. */
  isolation: isolate;
`;

/* ---- Top bar ---------------------------------------------------------- */
const TopBar = styled.div`
  ${tw`flex items-center flex-nowrap`}
  height: 48px;
  padding: 0 12px;
  background: ${TOPBAR};
  border-bottom: 1px solid #93c2c2;
  gap: 10px;
  overflow: hidden;
  @media (max-width: 720px) {
    gap: 8px;
    padding: 0 10px;
  }
`;
const LogoImg = styled.img`
  width: 112px;
  height: auto;
  flex-shrink: 0;
  @media (max-width: 720px) {
    width: 84px;
  }
`;
const HomeLink = styled.span`
  ${tw`font-semibold`}
  color: ${BLUE};
  font-size: 12px;
  flex-shrink: 0;
  @media (max-width: 720px) {
    display: none;
  }
`;
const Search = styled.div`
  ${tw`flex items-center rounded-full`}
  background: #eef8f8;
  border: 1px solid #bcd9d9;
  color: #6b8a8a;
  font-size: 12px;
  padding: 5px 12px;
  gap: 7px;
  width: 300px;
  max-width: 34%;
  min-width: 0;
  flex-shrink: 1;
  @media (max-width: 720px) {
    display: none;
  }
`;
const NextMsg = styled.div`
  ${tw`inline-flex items-center rounded-full font-semibold`}
  background: #eef8f8;
  border: 1px solid #bcd9d9;
  color: #3a5f5e;
  font-size: 12px;
  padding: 4px 12px;
  gap: 6px;
  flex-shrink: 0;
  span { color: ${BLUE}; }
  @media (max-width: 720px) {
    display: none;
  }
`;
const TopBarSpacer = styled.div`
  flex: 1;
  min-width: 8px;
`;
const TopBarRight = styled.div`
  ${tw`inline-flex items-center`}
  gap: 10px;
  flex-shrink: 0;
  @media (max-width: 720px) {
    gap: 6px;
  }
`;
const TopIcon = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full`}
  width: 22px;
  height: 22px;
  color: #3a5f5e;
  font-size: 14px;
  @media (max-width: 720px) {
    display: none;
  }
`;
const ModeIcon = styled(Brightness4)`
  color: #3a5f5e;
  cursor: pointer;
  font-size: 20px !important;
  margin-left: 4px;
`;
const HeadAvatar = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-bold text-white`}
  width: 26px;
  height: 26px;
  font-size: 10px;
  background: ${(p) => p.bg || '#64748b'};
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #93c2c2;
`;

/* ---- Layout body ------------------------------------------------------ */
const Body = tw.div`flex`;

/* ---- Sidebar ---------------------------------------------------------- */
const Sidebar = styled.div`
  background: ${SIDEBAR};
  color: ${HEADING_TEAL};
  width: 188px;
  flex-shrink: 0;
  padding: 12px 10px;
  @media (max-width: 720px) {
    display: none;
  }
`;
const WsHeader = styled.div`
  ${tw`flex items-center font-bold`}
  font-size: 13px;
  color: ${INK};
  padding: 2px 6px 10px;
  gap: 4px;
`;
const SideHeading = styled.div`
  ${tw`flex items-center font-bold uppercase`}
  font-size: 10px;
  letter-spacing: 0.06em;
  color: #487270;
  margin: 12px 6px 4px;
  gap: 4px;
  span.plus { margin-left: auto; font-size: 13px; }
`;
const SideItem = styled.div`
  ${tw`flex items-center rounded-md`}
  font-size: 12.5px;
  font-weight: ${(p) => (p.selected ? 700 : 500)};
  color: ${(p) => (p.selected ? INK : HEADING_TEAL)};
  background: ${(p) => (p.selected ? '#ffffff' : 'transparent')};
  box-shadow: ${(p) => (p.selected ? '0 1px 2px rgba(31,41,51,0.12)' : 'none')};
  padding: 5px 7px;
  gap: 7px;
  margin: 1px 0;
`;
const SideDot = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-bold text-white`}
  width: 17px;
  height: 17px;
  font-size: 9px;
  flex-shrink: 0;
  background: ${(p) => p.bg || '#5b7e7b'};
`;
const SideLabel = tw.span`flex-1 truncate`;
const SideBadge = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-bold`}
  min-width: 17px;
  height: 17px;
  font-size: 9px;
  padding: 0 5px;
  background: #ffffff;
  color: ${(p) => p.text || CHIP_TEAL.text};
  border: 1.5px solid ${(p) => p.border || CHIP_TEAL.border};
`;
const Kebab = styled.span`
  color: #487270;
  font-size: 13px;
  margin-left: 2px;
`;
const CollabRow = styled.div`
  ${tw`flex items-center`}
  padding: 2px 6px;
`;
const MiniAv = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-bold text-white`}
  width: 22px;
  height: 22px;
  font-size: 9px;
  background: ${(p) => p.bg};
  border: 2px solid ${SIDEBAR};
  margin-left: ${(p) => (p.overlap ? '-7px' : '0')};
`;

/* ---- Main area -------------------------------------------------------- */
const Main = styled.div`
  ${tw`flex-1`}
  background: ${BOARD};
  min-width: 0;
`;
const TabStrip = styled.div`
  ${tw`flex items-center flex-nowrap`}
  gap: 22px;
  background: #eaf6f6;
  border-bottom: 1px solid #bcd9d9;
  padding: 0 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: 720px) {
    gap: 0;
    justify-content: space-around;
    padding: 0 6px;
    overflow-x: visible;
  }
`;
const Tab = styled.div`
  ${tw`flex items-center font-semibold flex-shrink-0`}
  font-size: 13px;
  gap: 6px;
  padding: 11px 0 9px;
  color: ${(p) => (p.active ? INK : '#5b8482')};
  border-bottom: 2px solid ${(p) => (p.active ? BLUE : 'transparent')};
  margin-bottom: -1px;
  white-space: nowrap;
  @media (max-width: 720px) {
    ${tw`flex-1 justify-center`}
    font-size: 12px;
    padding: 8px 0 6px;
    gap: 0;
  }
`;
const TabIcon = styled.span`
  display: none;
  @media (max-width: 720px) {
    ${tw`inline-flex items-center justify-center relative`}
    color: ${(p) => (p.active ? BLUE : '#5b8482')};
    svg {
      font-size: 24px;
    }
  }
`;
const TabIconBadge = styled.span`
  ${tw`absolute inline-flex items-center justify-center rounded-full font-bold`}
  display: none;
  top: -4px;
  right: -10px;
  min-width: 15px;
  height: 15px;
  font-size: 9px;
  padding: 0 4px;
  color: ${CHIP_RED.text};
  background: #ffffff;
  border: 1.5px solid ${CHIP_RED.border};
  @media (max-width: 720px) {
    display: inline-flex;
  }
`;
const TabLabel = styled.span`
  ${tw`inline-flex items-center`}
  gap: 6px;
  @media (max-width: 720px) {
    display: none;
  }
`;
const NewBadge = styled.span`
  ${tw`inline-flex items-center rounded-full font-bold`}
  font-size: 10px;
  padding: 1px 7px;
  color: ${CHIP_RED.text};
  background: #ffffff;
  border: 1.5px solid ${CHIP_RED.border};
`;

/* ---- Scene stage ------------------------------------------------------ */
// The board is the tallest scene. It stays mounted (just hidden) so the
// container height never changes when the wizard takes over (T-all-2203
// feedback: height jumps between scenes are distracting).
const Stage = styled.div`
  position: relative;
`;

/* ---- Board (scene 1) -------------------------------------------------- */
const BoardArea = styled.div`
  padding: 14px 16px 16px;
  visibility: ${(p) => (p.$dim ? 'hidden' : 'visible')};
`;
const AddJob = styled.button`
  ${tw`inline-flex items-center rounded-full font-bold`}
  color: ${BLUE};
  background: #ffffff;
  border: 1.5px solid ${BLUE};
  font-size: 12.5px;
  padding: 6px 18px;
  gap: 6px;
  margin-bottom: 14px;
  cursor: pointer;
`;
const Backlog = styled.div`
  ${tw`grid`}
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 16px;
  @media (min-width: 720px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;
const MiniCard = styled.div`
  ${tw`rounded-lg bg-white`}
  border: 1px solid ${CARD_BORDER};
  padding: 8px 9px;
`;
const MiniMeta = styled.div`
  ${tw`flex items-center`}
  gap: 5px;
  margin-bottom: 5px;
`;
const PersonIcon = styled.span`
  ${tw`inline-flex items-center justify-center rounded`}
  width: 17px;
  height: 17px;
  border: 1px solid #9ad0ae;
  color: ${GREEN};
  flex-shrink: 0;
`;
const TimeText = styled.span`
  color: #6b8a8a;
  font-size: 10px;
`;
const WsTag = styled.div`
  color: #5b8482;
  font-size: 10px;
  font-weight: 600;
  gap: 5px;
`;
const MiniTitle = styled.div`
  color: ${INK};
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.25;
`;
const Columns = styled.div`
  ${tw`grid`}
  grid-template-columns: 1fr;
  gap: 12px;
  @media (min-width: 720px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const Col = styled.div`
  ${tw`flex flex-col`}
  gap: 10px;
`;
const ColHeader = styled.div`
  ${tw`flex items-center font-bold`}
  color: ${INK};
  font-size: 12.5px;
  gap: 6px;
  height: 22px;
  padding: 0 2px;
`;
const ColHeaderSpacer = styled.div`
  height: 22px;
`;
const Card = styled.div`
  ${tw`rounded-lg bg-white`}
  border: 1px solid ${CARD_BORDER};
  box-shadow: 0 1px 2px rgba(31, 41, 51, 0.05);
  padding: 10px 11px;
`;
const CardMeta = styled.div`
  ${tw`flex items-center flex-wrap`}
  gap: 6px;
  margin-bottom: 6px;
`;
const AvCluster = styled.span`
  ${tw`inline-flex items-center`}
  flex-shrink: 0;
`;
const StatusAv = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-bold text-white`}
  width: 16px;
  height: 16px;
  font-size: 8px;
  background: ${(p) => p.bg};
  border: 1.5px solid #fff;
  margin-left: ${(p) => (p.overlap ? '-5px' : '0')};
`;
const Status = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  color: ${(p) => p.color || '#6b8a8a'};
`;
const CountBadge = styled.span`
  ${tw`inline-flex items-center rounded-full font-bold`}
  font-size: 10px;
  padding: 1px 8px;
  color: ${(p) => p.text};
  background: #ffffff;
  border: 1.5px solid ${(p) => p.border};
`;
const CardTitle = styled.div`
  color: ${INK};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
`;
const DeployChip = styled.span`
  ${tw`inline-flex items-center rounded`}
  font-size: 10px;
  font-weight: 600;
  color: #4a5568;
  background: transparent;
  border: 1px solid #b7cdcd;
  padding: 2px 7px;
  margin-top: 6px;
`;
const NoteLine = styled.div`
  ${tw`rounded`}
  margin-top: 7px;
  padding: 5px 8px;
  font-size: 10.5px;
  color: #4a5568;
  background: #f5fafa;
  border-left: 3px solid ${BLUE};
  line-height: 1.3;
`;

/* ---- Wizard (scene 2) ------------------------------------------------- */
const WizardArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${BOARD};
  padding: 22px 28px 28px;
  animation: wiz-in 0.32s ease both;
  @keyframes wiz-in {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;
const WizHeading = styled.div`
  color: ${INK};
  font-weight: 800;
  font-size: 26px;
  line-height: 1.15;
`;
const WizSub = styled.div`
  color: #466;
  font-size: 13px;
  margin-top: 8px;
`;
const Pills = styled.div`
  ${tw`flex items-center`}
  flex-wrap: nowrap;
  gap: 8px;
  margin: 16px 0 12px;
`;
const Pill = styled.span`
  ${tw`inline-flex items-center rounded-full font-semibold`}
  font-size: 12.5px;
  padding: 5px 16px;
  color: ${(p) => (p.active ? '#fff' : '#37606a')};
  background: ${(p) => (p.active ? WIZARD_BLUE : '#ffffff')};
  border: 1px solid ${(p) => (p.active ? WIZARD_BLUE : '#c2dedc')};
  flex-shrink: 0;
  white-space: nowrap;
  @media (max-width: 720px) {
    font-size: 11px;
    padding: 5px 11px;
  }
`;
const EditorBox = styled.div`
  ${tw`rounded-md bg-white`}
  border: 1px solid #d4dde0;
  max-width: 430px;
  overflow: hidden;
`;
const EditorBody = styled.div`
  padding: 10px 12px;
  color: ${INK};
  font-size: 12.5px;
  line-height: 1.45;
`;
const IntroLine = styled.p`
  margin: 0;
`;
const Bullets = styled.ul`
  list-style-type: disc;
  list-style-position: outside;
  margin: 6px 0 0;
  padding: 0 0 0 1.25rem;
`;
const BulletItem = styled.li`
  display: list-item;
  margin: 2px 0;
`;
const Toolbar = styled.div`
  ${tw`flex items-center flex-wrap`}
  gap: 9px;
  border-top: 1px solid #e2e8ea;
  background: #fbfdfd;
  padding: 6px 10px;
  color: #5b6b6b;
  font-size: 12px;
`;
const ToolGroup = styled.span`
  ${tw`inline-flex items-center`}
  gap: 7px;
`;
const ToolSelect = styled.span`
  ${tw`inline-flex items-center rounded`}
  border: 1px solid #e2e8ea;
  padding: 2px 6px;
  font-size: 11px;
  gap: 6px;
  color: #5b6b6b;
`;
const ToolDivider = styled.span`
  width: 1px;
  height: 14px;
  background: #e2e8ea;
`;
const WizButtons = styled.div`
  ${tw`flex items-center`}
  flex-wrap: nowrap;
  gap: 18px;
  margin-top: 22px;
  @media (max-width: 720px) {
    gap: 10px;
  }
`;
const WizPrimary = styled.span`
  ${tw`inline-flex items-center justify-center rounded-full font-semibold text-white`}
  background: ${WIZARD_BLUE};
  font-size: 13px;
  height: 38px;
  width: 190px;
  flex-shrink: 0;
  white-space: nowrap;
  @media (max-width: 720px) {
    width: auto;
    font-size: 12px;
    height: 36px;
    padding: 0 14px;
  }
`;
const WizCancel = styled.span`
  ${tw`inline-flex items-center`}
  color: #37606a;
  font-size: 13px;
  font-weight: 600;
  height: 38px;
  flex-shrink: 0;
  white-space: nowrap;
  @media (max-width: 720px) {
    font-size: 12px;
    height: 36px;
  }
`;

/* ---- Animated cursor -------------------------------------------------- */
const CursorWrap = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 40;
  pointer-events: none;
  transform: translate(${(p) => p.x}px, ${(p) => p.y}px);
  transition: transform ${(p) => (p.instant ? '0s' : `${CURSOR_MOVE}ms`)} cubic-bezier(0.45, 0, 0.25, 1);
`;
const CursorRipple = styled.span`
  position: absolute;
  left: -3px;
  top: -3px;
  width: 26px;
  height: 26px;
  border-radius: 9999px;
  border: 2px solid ${WIZARD_BLUE};
  opacity: 0;
  ${(p) => p.clicking && `
    animation: ripple 0.45s ease-out;
  `}
  @keyframes ripple {
    from { opacity: 0.7; transform: scale(0.3); }
    to   { opacity: 0; transform: scale(1.6); }
  }
`;

const SearchGlyph = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="11" cy="11" r="7" stroke="#6b8a8a" strokeWidth="2" />
        <path d="M21 21l-4.3-4.3" stroke="#6b8a8a" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const PersonGlyph = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4 0-8 2-8 5v1h16v-1c0-3-4-5-8-5z" />
    </svg>
);
const PointerGlyph = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 3l14 8-6 1.5L9 19 5 3z" fill="#1f2933" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
);

const PlanAnimation = () => {
    const appRef = useRef(null);
    const addJobRef = useRef(null);
    // phase: 'board' -> 'moving' -> 'click' -> 'wizard' -> 'board' ...
    const [phase, setPhase] = useState('board');
    const [cursor, setCursor] = useState({ x: 0, y: 0, instant: true, visible: false });

    const restPos = () => {
        const r = appRef.current?.getBoundingClientRect();
        if (!r) return { x: 0, y: 0 };
        return { x: r.width * 0.62, y: r.height * 0.6 };
    };

    useLayoutEffect(() => {
        const { x, y } = restPos();
        setCursor({ x, y, instant: true, visible: true });
    }, []);

    useEffect(() => {
        const app = appRef.current;
        if (!app) return undefined;
        let timer;
        if (phase === 'board') {
            const { x, y } = restPos();
            setCursor({ x, y, instant: true, visible: true });
            timer = setTimeout(() => setPhase('moving'), BOARD_DWELL);
        } else if (phase === 'moving') {
            const appRect = app.getBoundingClientRect();
            const btn = addJobRef.current?.getBoundingClientRect();
            if (btn) {
                setCursor({
                    x: btn.left - appRect.left + btn.width / 2,
                    y: btn.top - appRect.top + btn.height / 2,
                    instant: false,
                    visible: true,
                });
            }
            timer = setTimeout(() => setPhase('click'), CURSOR_MOVE);
        } else if (phase === 'click') {
            timer = setTimeout(() => setPhase('wizard'), CLICK_HOLD);
        } else if (phase === 'wizard') {
            timer = setTimeout(() => setPhase('board'), WIZARD_DWELL);
        }
        return () => clearTimeout(timer);
    }, [phase]);

    const showWizard = phase === 'wizard';
    const clicking = phase === 'click';

    return (
        <Wrap>
            <Backdrop>
                <AppWindow ref={appRef}>
                    <TopBar>
                        <LogoImg src={logo} alt="Uclusion" />
                        <HomeLink>Home</HomeLink>
                        <Search>
                            <SearchGlyph />
                            Search David Israel
                        </Search>
                        <TopBarSpacer />
                        <NextMsg>Next task <span>→</span></NextMsg>
                        <TopBarRight>
                            <TopIcon>?</TopIcon>
                            <ModeIcon titleAccess="Light / dark mode" />
                            <HeadAvatar bg="#4c5b8a">DI</HeadAvatar>
                        </TopBarRight>
                    </TopBar>

                    <Body>
                        <Sidebar>
                            <WsHeader>Uclusion <span style={{ fontSize: 11 }}>▾</span></WsHeader>

                            <SideHeading>Views <span className="plus">+</span></SideHeading>
                            <SideItem>
                                <SideDot bg="#5b7e7b">▦</SideDot>
                                <SideLabel>Marketing</SideLabel>
                                <SideBadge text={CHIP_TEAL.text} border={CHIP_TEAL.border}>1</SideBadge>
                            </SideItem>
                            <SideItem selected>
                                <SideDot bg="#4c5b8a">DI</SideDot>
                                <SideLabel>David Israel</SideLabel>
                                <Kebab>⋮</Kebab>
                            </SideItem>

                            <SideItem style={{ marginTop: 8 }}>
                                <span style={{ fontSize: 13 }}>✎</span>
                                <SideLabel>Compose</SideLabel>
                            </SideItem>

                            <SideHeading>Collaborators <span className="plus">+</span></SideHeading>
                            <CollabRow>
                                <MiniAv bg="#4c5b8a">DI</MiniAv>
                                <MiniAv bg={BLUE} overlap>AI</MiniAv>
                            </CollabRow>

                            <SideHeading>Messages <span className="plus">⌃</span></SideHeading>
                            <SideItem>
                                <span style={{ fontSize: 12 }}>✉</span>
                                <SideLabel>You able</SideLabel>
                                <SideBadge text={CHIP_RED.text} border={CHIP_RED.border}>2</SideBadge>
                            </SideItem>
                            <SideItem>
                                <span style={{ fontSize: 12 }}>✉</span>
                                <SideLabel>Pokable</SideLabel>
                            </SideItem>

                            <SideHeading>Integrations <span className="plus">⌃</span></SideHeading>
                            <SideItem><span style={{ fontSize: 12 }}>⚡</span><SideLabel>AI Connection</SideLabel></SideItem>
                            <SideItem><span style={{ fontSize: 12 }}>#</span><SideLabel>Slack</SideLabel></SideItem>

                            <SideHeading>Other workspaces <span className="plus">+</span></SideHeading>
                            <SideItem><span style={{ fontSize: 12 }}>❖</span><SideLabel>Support</SideLabel></SideItem>
                        </Sidebar>

                        <Main>
                            <TabStrip>
                                {BOARD_TABS.map((tab) => {
                                    const Icon = tab.icon;
                                    return (
                                        <Tab key={tab.label} active={tab.active}>
                                            <TabIcon active={tab.active} aria-hidden="true">
                                                <Icon />
                                                {tab.mobileTag && <TabIconBadge>{tab.mobileTag}</TabIconBadge>}
                                            </TabIcon>
                                            <TabLabel>
                                                {tab.label}
                                                {tab.desktopTag && (
                                                    <NewBadge>{tab.desktopTag}</NewBadge>
                                                )}
                                            </TabLabel>
                                        </Tab>
                                    );
                                })}
                            </TabStrip>

                            <Stage>
                                <BoardArea $dim={showWizard} aria-hidden={showWizard}>
                                    <AddJob ref={addJobRef}>+ Add job</AddJob>

                                    <Backlog>
                                        {[
                                            { t: 'Fix support workspace setup', a: '4 days ago' },
                                            { t: 'Trim memory on large boards', a: '6 days ago' },
                                            { t: 'Other technical debt', a: '7 days ago' },
                                            { t: 'Backend issues', a: '12 days ago' },
                                            { t: 'Post again on Product Hunt', a: '22 days ago', w: 'Marketing' },
                                        ].map((c, i) => (
                                            <MiniCard key={i}>
                                                {c.w && <WsTag>{c.w}</WsTag>}
                                                <MiniMeta>
                                                    <PersonIcon><PersonGlyph /></PersonIcon>
                                                    <TimeText>{c.a}</TimeText>
                                                </MiniMeta>
                                                <MiniTitle>{c.t}</MiniTitle>
                                            </MiniCard>
                                        ))}
                                    </Backlog>

                                    <Columns>
                                        <Col>
                                            <ColHeader><SideDot bg="#4c5b8a">DI</SideDot> David Israel</ColHeader>
                                            <Card>
                                                <CardMeta>
                                                    <AvCluster><StatusAv bg="#4c5b8a">DI</StatusAv></AvCluster>
                                                    <Status color="#b96f00">Paused</Status>
                                                    <TimeText>20 hours ago</TimeText>
                                                </CardMeta>
                                                <CardTitle>Split notes into AI‑visible and report‑visible fields</CardTitle>
                                            </Card>
                                            <Card>
                                                <CardMeta>
                                                    <AvCluster><StatusAv bg="#4c5b8a">DI</StatusAv></AvCluster>
                                                    <Status color="#b96f00">Paused</Status>
                                                    <TimeText>2 days ago</TimeText>
                                                    <WsTag>Marketing</WsTag>
                                                </CardMeta>
                                                <CardTitle>A tool for finding more developers like Victor</CardTitle>
                                            </Card>
                                            <Card>
                                                <CardMeta>
                                                    <AvCluster><StatusAv bg="#4c5b8a">DI</StatusAv></AvCluster>
                                                    <Status color="#b96f00">Paused</Status>
                                                    <TimeText>2 days ago</TimeText>
                                                </CardMeta>
                                                <CardTitle>Better drag and drop on swimlanes</CardTitle>
                                            </Card>
                                        </Col>

                                        <Col>
                                            <ColHeaderSpacer />
                                            <Card>
                                                <CardMeta>
                                                    <CountBadge text={CHIP_TEAL.text} border={CHIP_TEAL.border}>12 open</CountBadge>
                                                </CardMeta>
                                                <CardTitle>Bugs B‑all‑454, B‑all‑453, B‑all‑452, B‑all‑451, B‑all‑450, T‑all‑2104…</CardTitle>
                                            </Card>
                                            <Card>
                                                <CardMeta>
                                                    <CountBadge text={CHIP_RED.text} border={CHIP_RED.border}>1 new</CountBadge>
                                                    <CountBadge text={CHIP_TEAL.text} border={CHIP_TEAL.border}>5 open</CountBadge>
                                                </CardMeta>
                                                <CardTitle>Marketing feedback</CardTitle>
                                            </Card>
                                            <Card>
                                                <CardMeta>
                                                    <CountBadge text={CHIP_RED.text} border={CHIP_RED.border}>1 new</CountBadge>
                                                    <CountBadge text={CHIP_TEAL.text} border={CHIP_TEAL.border}>8 open</CountBadge>
                                                    <WsTag>Marketing</WsTag>
                                                </CardMeta>
                                                <CardTitle>Show like‑minded developers how to steer the AI flow</CardTitle>
                                            </Card>
                                        </Col>

                                        <Col>
                                            <ColHeaderSpacer />
                                            <Card>
                                                <CardMeta>
                                                    <AvCluster>
                                                        <StatusAv bg={GREEN}>DI</StatusAv>
                                                        <StatusAv bg={BLUE} overlap>AI</StatusAv>
                                                    </AvCluster>
                                                    <Status color={GREEN}>Complete</Status>
                                                    <TimeText>5 hours ago</TimeText>
                                                </CardMeta>
                                                <CardTitle>Open certain wizards inside their containers</CardTitle>
                                                <DeployChip>Deployed to stage</DeployChip>
                                                <NoteLine>Wizards now open inside their container instead of a full‑screen route. Lint is clean.</NoteLine>
                                            </Card>
                                            <Card>
                                                <CardMeta>
                                                    <AvCluster>
                                                        <StatusAv bg={GREEN}>DI</StatusAv>
                                                        <StatusAv bg={BLUE} overlap>AI</StatusAv>
                                                    </AvCluster>
                                                    <Status color={GREEN}>Complete</Status>
                                                    <TimeText>20 hours ago</TimeText>
                                                </CardMeta>
                                                <CardTitle>Better UI</CardTitle>
                                                <DeployChip>Deployed to production</DeployChip>
                                            </Card>
                                            <Card>
                                                <CardMeta>
                                                    <AvCluster>
                                                        <StatusAv bg={GREEN}>DI</StatusAv>
                                                        <StatusAv bg={BLUE} overlap>AI</StatusAv>
                                                    </AvCluster>
                                                    <Status color={GREEN}>Complete</Status>
                                                    <TimeText>2 days ago</TimeText>
                                                </CardMeta>
                                                <CardTitle>Better install</CardTitle>
                                                <DeployChip>Deployed to production</DeployChip>
                                                <NoteLine>T‑all‑2174 (refresh defaults to yes) implemented and resolved; install script now accepts a default.</NoteLine>
                                            </Card>
                                        </Col>
                                    </Columns>
                                </BoardArea>

                                {showWizard && (
                                <WizardArea>
                                    <WizHeading>How would you describe this job?</WizHeading>
                                    <WizSub>Fill the description below and the job name will be created automatically. A bullet list will become tasks.</WizSub>
                                    <Pills>
                                        <Pill active>Immediate</Pill>
                                        <Pill>Ready to assign</Pill>
                                        <Pill>Not ready</Pill>
                                    </Pills>
                                    <EditorBox>
                                        <EditorBody>
                                            <IntroLine>Use AI to write a walkthrough about using AI to create the walkthrough.</IntroLine>
                                            <Bullets>
                                                <BulletItem>First link on the header</BulletItem>
                                                <BulletItem>Answer AI questions in options</BulletItem>
                                                <BulletItem>Convert an AI suggestion to a task</BulletItem>
                                                <BulletItem>AI approves, completes, and asks for review</BulletItem>
                                            </Bullets>
                                        </EditorBody>
                                        <Toolbar>
                                            <ToolSelect>Sans Serif <span style={{ fontSize: 9 }}>▾</span></ToolSelect>
                                            <ToolSelect>Normal <span style={{ fontSize: 9 }}>▾</span></ToolSelect>
                                            <ToolDivider />
                                            <ToolGroup style={{ fontSize: 13 }}>
                                                <b>B</b>
                                                <i>I</i>
                                                <u>U</u>
                                                <s>S</s>
                                            </ToolGroup>
                                            <ToolDivider />
                                            <ToolGroup>
                                                <span>x₂</span>
                                                <span>x²</span>
                                            </ToolGroup>
                                            <ToolDivider />
                                            <ToolGroup>
                                                <span style={{ color: '#c0392b', fontWeight: 700 }}>A</span>
                                                <span style={{ background: '#f4e04d', padding: '0 2px', fontWeight: 700 }}>A</span>
                                            </ToolGroup>
                                            <ToolDivider />
                                            <ToolGroup>
                                                <span>≣</span>
                                                <span>≡</span>
                                                <span>⤢</span>
                                            </ToolGroup>
                                        </Toolbar>
                                    </EditorBox>
                                    <WizButtons>
                                        <WizPrimary>Create job</WizPrimary>
                                        <WizPrimary>Use approvals</WizPrimary>
                                        <WizCancel>Cancel</WizCancel>
                                    </WizButtons>
                                </WizardArea>
                                )}
                            </Stage>
                        </Main>
                    </Body>

                    {cursor.visible && (
                        <CursorWrap x={cursor.x} y={cursor.y} instant={cursor.instant}>
                            <CursorRipple clicking={clicking} />
                            <PointerGlyph />
                        </CursorWrap>
                    )}
                </AppWindow>
            </Backdrop>
        </Wrap>
    );
};

export default PlanAnimation;
